import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useGlobalContext } from "../context/AuthContext";
import { LoginError } from "../context/AuthContext";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { Modal } from "antd";
import { getApiBaseUrl } from "../../config/runtime";

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, googleLogin } = useGlobalContext();
  const { token } = useParams<{ token: string }>();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const [loginError, setLoginError] = useState<{
    code: "unverified" | "bad_credentials" | "server_error" | "network_error" | "google_failed" | null;
    message: string;
  } | null>(null);

  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);

  const verifyEmail = useCallback(async (authToken: string) => {
    try {
      const response = await axios.post(
        `${apiBaseUrl}/verify/verify-email`,
        { authToken },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.status === 200) {
        Modal.success({
          title: "Email verified",
          content: "Your email has been verified. You can sign in now.",
        });
        return;
      }
    } catch (error) {
      console.error("Error verifying email:", error);
      Modal.error({
        title: "Verification failed",
        content: "The verification link is invalid or has expired. Please request a new one.",
        onOk() { navigate("/login"); },
      });
    }
  }, [apiBaseUrl, navigate]);

  // ── Handle OAuth Errors or Verification Tokens ───────────────────────────
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get("error") === "google_failed") {
      setLoginError({
        code: "google_failed",
        message: "Google authentication failed. Please try again or use your email and password.",
      });
      // Clean up the URL to prevent error persisting on manual refresh
      navigate("/login", { replace: true });
    }

    if (token) {
      void verifyEmail(token);
    }
  }, [token, location.search, navigate, verifyEmail]);

  const handleResendVerification = async () => {
    if (!email) {
      setLoginError({
        code: null,
        message: "Please enter your email address above, then click Resend.",
      });
      return;
    }

    setIsResending(true);
    try {
      await axios.post(
        `${apiBaseUrl}/verify/resend-verification-email`,
        { email: email.trim().toLowerCase() },
        { headers: { "Content-Type": "application/json" } }
      );
      Modal.success({
        title: "Verification email sent",
        content: "A new verification link has been sent. Please check your inbox.",
      });
    } catch (err) {
      const message =
        axios.isAxiosError(err) && err.response?.data?.message
          ? err.response.data.message
          : "Could not send verification email. Please try again later.";
      Modal.error({ title: "Send failed", content: message });
    } finally {
      setIsResending(false);
    }
  };

  const handleLocalLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    setIsLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      if (err instanceof LoginError) {
        setLoginError({ code: err.code as any, message: err.message });
      } else {
        setLoginError({ code: "server_error", message: "Login failed. Please try again." });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderLoginError = () => {
    if (!loginError) return null;

    const isWarning = loginError.code === "unverified";
    return (
      <div
        className="mt-4 p-4 rounded"
        style={{
          backgroundColor: isWarning ? "#fef9e7" : "#fdf2f2",
          border: `1px solid ${isWarning ? "#f5a623" : "var(--color-terracotta)"}`,
          color: isWarning ? "#7d4e00" : "var(--color-terracotta)",
          fontSize: 14,
        }}
      >
        <p className="mb-0">{loginError.message}</p>

        {loginError.code === "unverified" && (
          <button
            type="button"
            onClick={handleResendVerification}
            disabled={isResending}
            className="mt-3 text-sm font-medium underline"
            style={{ color: "#7d4e00", background: "none", border: "none", cursor: "pointer", padding: 0 }}
          >
            {isResending ? "Sending…" : "Resend verification email →"}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col px-4 pt-28 pb-12" style={{ backgroundColor: "var(--color-ivory)" }}>
      <div className="w-full max-w-md m-auto" style={{ fontFamily: "var(--font-primary)" }}>
        <div className="text-center mb-8">
          <h1 className="text-3xl font-semibold mb-2" style={{ color: "var(--color-forest)", letterSpacing: "var(--letter-spacing-heading)" }}>
            Welcome Back
          </h1>
          <p style={{ color: "var(--color-gray-warm)" }}>Sign in to continue to Gistoscope</p>
        </div>

        <div className="rounded-lg p-8" style={{ backgroundColor: "white", border: "1px solid var(--color-border)", boxShadow: "var(--shadow-subtle)" }}>
          <button
            onClick={() => { setIsGoogleLoading(true); googleLogin(); }}
            disabled={isGoogleLoading}
            className="w-full flex items-center justify-center gap-3 py-3 px-4 mb-6 transition-all duration-200"
            style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", backgroundColor: "white", color: "var(--color-charcoal)", opacity: isGoogleLoading ? 0.7 : 1 }}
          >
            {isGoogleLoading ? (
               <span className="font-medium italic">Connecting...</span>
            ) : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                <span className="font-medium">Continue with Google</span>
              </>
            )}
          </button>

          <div className="flex items-center mb-6">
            <div className="flex-1 border-t" style={{ borderColor: "var(--color-border)" }} />
            <span className="px-4 text-sm" style={{ color: "var(--color-gray-warm)" }}>or</span>
            <div className="flex-1 border-t" style={{ borderColor: "var(--color-border)" }} />
          </div>

          <form onSubmit={handleLocalLogin}>
            <div className="mb-5">
              <label className="block text-sm font-medium mb-2" style={{ color: "var(--color-charcoal)" }}>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setLoginError(null); }}
                className="w-full px-4 py-3"
                style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)" }}
                placeholder="you@example.com"
                required
              />
            </div>

            <div className="mb-5">
              <label className="block text-sm font-medium mb-2" style={{ color: "var(--color-charcoal)" }}>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setLoginError(null); }}
                className="w-full px-4 py-3"
                style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)" }}
                placeholder="••••••••"
                required
              />
            </div>

            <div className="flex justify-end mb-6">
              <button type="button" onClick={() => navigate("/forgot-password")} className="text-sm" style={{ color: "var(--color-forest)" }}>
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-4 font-medium text-white transition-all duration-200"
              style={{ backgroundColor: "var(--color-terracotta)", borderRadius: "var(--radius-md)", opacity: isLoading ? 0.7 : 1 }}
            >
              {isLoading ? "Signing in..." : "Sign In"}
            </button>
          </form>

          {renderLoginError()}

          <p className="mt-6 text-center text-sm" style={{ color: "var(--color-gray-warm)" }}>
            Don't have an account?{" "}
            <Link to="/signup" className="font-medium" style={{ color: "var(--color-forest)" }}>Create one</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
